import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./PounceSettings.module.css";
import {
    getPounceAgentSettings,
    updatePounceAgentSettings,
    getPounceStatus,
    setPounceEnabled
} from "../../Store/apiStore";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TIMEZONE_OPTIONS = [
    // US & Canada
    { value: "America/New_York", label: "Eastern Time (ET)", sub: "New York, Toronto, Miami, Atlanta" },
    { value: "America/Chicago", label: "Central Time (CT)", sub: "Chicago, Dallas, Houston, Austin" },
    { value: "America/Denver", label: "Mountain Time (MT)", sub: "Denver, Salt Lake City, Calgary" },
    { value: "America/Phoenix", label: "Arizona (MST, no DST)", sub: "Phoenix" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)", sub: "Los Angeles, San Francisco, Seattle, Vancouver" },
    { value: "America/Anchorage", label: "Alaska Time (AKST)", sub: "Anchorage" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HST)", sub: "Honolulu" },
    { value: "America/Mexico_City", label: "Central Time (Mexico)", sub: "Mexico City" },
    // Europe & UK
    { value: "Europe/London", label: "London, Dublin (GMT/BST)", sub: "UK & Ireland" },
    { value: "Europe/Paris", label: "Paris, Berlin, Rome, Madrid (CET)", sub: "Central Europe" },
    { value: "Europe/Amsterdam", label: "Amsterdam, Brussels, Zurich (CET)", sub: "Central Europe" },
    { value: "Europe/Istanbul", label: "Istanbul (TRT)", sub: "Turkey" },
    // Asia & Middle East
    { value: "Asia/Dubai", label: "Gulf Standard Time (GST)", sub: "Dubai, Abu Dhabi, Riyadh" },
    { value: "Asia/Kolkata", label: "India Standard Time (IST)", sub: "Mumbai, Delhi, Bengaluru" },
    { value: "Asia/Singapore", label: "Singapore Time (SGT)", sub: "Singapore, Kuala Lumpur" },
    { value: "Asia/Hong_Kong", label: "Hong Kong, Beijing (HKT/CST)", sub: "China" },
    { value: "Asia/Tokyo", label: "Japan & Korea (JST)", sub: "Tokyo, Seoul" },
    { value: "Asia/Bangkok", label: "Indochina Time (ICT)", sub: "Bangkok, Jakarta" },
    // Australia & Pacific
    { value: "Australia/Sydney", label: "Sydney, Melbourne (AEST)", sub: "Eastern Australia" },
    { value: "Australia/Perth", label: "Perth (AWST)", sub: "Western Australia" },
    { value: "Pacific/Auckland", label: "Auckland, Wellington (NZST)", sub: "New Zealand" },
    // Latin America & Africa
    { value: "America/Sao_Paulo", label: "São Paulo, Rio (BRT)", sub: "Brazil" },
    { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires (ART)", sub: "Argentina" },
    { value: "Africa/Johannesburg", label: "South Africa (SAST)", sub: "Johannesburg, Cape Town" },
    { value: "Africa/Cairo", label: "Cairo (EET)", sub: "Egypt" },
];

const DEFAULT_HOURS = {
    timezone: "America/New_York",
    sameSchedule: false,
    schedule: {
        Monday: { open: "09:00", close: "18:00", closed: false },
        Tuesday: { open: "09:00", close: "18:00", closed: false },
        Wednesday: { open: "09:00", close: "18:00", closed: false },
        Thursday: { open: "09:00", close: "18:00", closed: false },
        Friday: { open: "09:00", close: "18:00", closed: false },
        Saturday: { open: "10:00", close: "16:00", closed: true },
        Sunday: { open: "10:00", close: "16:00", closed: true },
    }
};

const DEFAULT_SMS_TEMPLATE =
    "Hi {name}, we just tried calling you regarding your inquiry. We'll try again soon!";
const SMS_MAX_CHARS = 320;

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
        const hour12 = h % 12 || 12;
        const ampm = h < 12 ? "AM" : "PM";
        const mm = m.toString().padStart(2, "0");
        TIME_OPTIONS.push({
            value: `${h.toString().padStart(2, "0")}:${mm}`,
            label: `${hour12}:${mm} ${ampm}`
        });
    }
}

const SLOT_STEP_MIN = 15;

function timeToMinutes(hhmm) {
    const [h, m] = (hhmm || "00:00").split(":").map(Number);
    return h * 60 + m;
}

function minutesToTime(mins) {
    const safe = ((mins % (24 * 60)) + (24 * 60)) % (24 * 60);
    const h = Math.floor(safe / 60);
    const m = safe % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const RETRY_MAX = 4;
const LEGAL_START_MIN = 8 * 60;
const LEGAL_END_MIN = 21 * 60;

const AFTER_OPTIONS = [
    { value: 30, label: "30 minutes later" },
    { value: 60, label: "1 hour later" },
    { value: 120, label: "2 hours later" },
    { value: 180, label: "3 hours later" },
    { value: 240, label: "4 hours later" },
    { value: 360, label: "6 hours later" },
    { value: "nextWindow", label: "Next calling window" },
];
const AFTER_MINUTES = AFTER_OPTIONS.filter(o => typeof o.value === "number").map(o => o.value);

const DAY_OPTIONS = [
    { value: 0, label: "Same day" },
    { value: 1, label: "Next day" },
    { value: 2, label: "2 days later" },
    { value: 3, label: "3 days later" },
    { value: "nextWindow", label: "Next calling window" },
];

const DEFAULT_RETRY_ITEMS = [
    { type: "after", minutes: 120 },
    { type: "day", dayOffset: 1, time: "open" },
    { type: "day", dayOffset: 2, time: "open" },
    { type: "day", dayOffset: 1, time: "open" },
];
const DEFAULT_RETRY_COUNT = 4;
const LEGACY_UNSET_GAPS = [2, 10, 30];

const WEEKDAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

function tzOffsetMs(date, tz) {
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: tz, hour12: false,
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        }).formatToParts(date);
        const p = {};
        parts.forEach(part => { p[part.type] = part.value; });
        const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
        return asUTC - date.getTime();
    } catch {
        return 0;
    }
}

function wallToUtc(tz, y, mo, d, h, mi) {
    const naive = Date.UTC(y, mo, d, h, mi, 0, 0);
    let ts = naive;
    for (let i = 0; i < 3; i++) {
        const next = naive - tzOffsetMs(new Date(ts), tz);
        if (next === ts) break;
        ts = next;
    }
    return new Date(ts);
}

function toWall(date, tz) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, hour12: false,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", weekday: "short",
    }).formatToParts(date);
    const p = {};
    parts.forEach(part => { p[part.type] = part.value; });
    const weekday = WEEKDAY_INDEX[p.weekday] ?? 0;
    return { y: +p.year, mo: +p.month - 1, d: +p.day, h: +p.hour % 24, mi: +p.minute, weekday };
}

function formatTime12h(time24) {
    if (!time24) return "–:––";
    const [h, m] = time24.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
}

function addDays({ y, mo, d }, days) {
    const dt = new Date(Date.UTC(y, mo, d, 12));
    dt.setUTCDate(dt.getUTCDate() + days);
    return { y: dt.getUTCFullYear(), mo: dt.getUTCMonth(), d: dt.getUTCDate() };
}

const hoursTimezone = (hours) => (hours && hours.timezone) || DEFAULT_HOURS.timezone;

/**
 * The open window (in minutes) for one weekday, already clamped to the legal
 * 8:00 AM – 9:00 PM band.  Returns null when the day is closed or has no
 * usable time inside the legal band.
 *
 * "Same Schedule Everyday" only reuses the TIMES of the first open day —
 * each day keeps its own checkbox, so an unchecked Saturday stays closed.
 */
function scheduleWindow(hours, dayName, { legal = true } = {}) {
    const schedule = hours?.schedule || {};
    let entry = schedule[dayName];
    if (!entry) return null;

    if (hours?.sameSchedule) {
        const reference = DAYS.map(d => schedule[d]).find(d => d && !d.closed);
        if (reference) entry = { ...reference, closed: entry.closed };
    }

    if (entry.closed) return null;

    let open = timeToMinutes(entry.open);
    let close = timeToMinutes(entry.close);
    if (!Number.isFinite(open) || !Number.isFinite(close) || close <= open) return null;
    if (legal) {
        open = Math.max(open, LEGAL_START_MIN);
        close = Math.min(close, LEGAL_END_MIN);
        if (close <= open) return null;
    }
    return { open, close };
}

function isOpenAt(hours, date) {
    const tz = hoursTimezone(hours);
    const w = toWall(date, tz);
    const win = scheduleWindow(hours, DAYS[w.weekday]);
    if (!win) return false;
    const mins = w.h * 60 + w.mi;
    return mins >= win.open && mins < win.close;
}

/** The next moment a calling window opens (strictly after `after`), or null. */
function nextWindowStart(hours, after) {
    const tz = hoursTimezone(hours);
    const w = toWall(after, tz);
    for (let i = 0; i <= 7; i++) {
        const dayName = DAYS[(w.weekday + i) % 7];
        const win = scheduleWindow(hours, dayName);
        if (!win) continue;
        const day = addDays(w, i);
        const start = wallToUtc(tz, day.y, day.mo, day.d, Math.floor(win.open / 60), win.open % 60);
        if (start.getTime() > after.getTime()) return start;
    }
    return null;
}

/** Moves a moment out of closed hours to the next open time of a calling day. */
function snapIntoWindow(hours, date) {
    if (isOpenAt(hours, date)) return new Date(date);
    return nextWindowStart(hours, date) || new Date(date);
}

/* How many days the resolver may walk forward before it gives up and falls
   back to the next window opening. */
const MAX_DAY_WALK = 14;

/**
 * Resolves ONE retry item into the real date-time it will fire.
 *
 * EVERY retry is measured from the PREVIOUS attempt (`from`), never from the
 * 1st call — so "Next day" always means "the day after the last attempt".
 *
 *  - "after N minutes"  → previous attempt + N, pushed into the next window
 *  - "N days later @ T" → T on (previous attempt's day + N); if that day is
 *                          closed or T has already passed, the SAME time on a
 *                          later day is tried (Sat 2 PM → Mon 2 PM), so the
 *                          picked clock time is never thrown away
 *  - "nextWindow"       → the next time a calling window opens
 */
function resolveRetryTime(hours, from, item) {
    if (!item) return from;

    if (item.type === "nextWindow") {
        return nextWindowStart(hours, from) || from;
    }

    if (item.type === "after") {
        return snapIntoWindow(hours, new Date(from.getTime() + Number(item.minutes || 0) * 60000));
    }

    const tz = hoursTimezone(hours);
    const w = toWall(from, tz);
    const isDynamicOpen = !item.time || item.time === "open";
    const [h, mi] = isDynamicOpen ? [0, 0] : String(item.time).split(":").map(Number);
    const mins = isDynamicOpen ? null : (h || 0) * 60 + (mi || 0);
    const offset = Number(item.dayOffset) || 0;

    for (let i = 0; i <= MAX_DAY_WALK; i++) {
        const step = offset + i;
        const win = scheduleWindow(hours, DAYS[(w.weekday + step) % 7]);
        if (!win) continue;                                   // closed day → try the next one
        const targetMins = isDynamicOpen ? win.open : mins;
        if (targetMins < win.open || targetMins > win.close) continue;    // picked time not in that day's hours
        const day = addDays(w, step);
        const targetH = Math.floor(targetMins / 60);
        const targetMi = targetMins % 60;
        const target = wallToUtc(tz, day.y, day.mo, day.d, targetH, targetMi);
        if (target.getTime() <= from.getTime()) continue;     // must be after the previous attempt
        return target;
    }
    return nextWindowStart(hours, from) || from;
}

function resolveLadder(hours, start, items) {
    const out = [];
    let cursor = start;
    items.forEach(item => {
        const t = resolveRetryTime(hours, cursor, item);
        out.push(t);
        cursor = t;
    });
    return out;
}

function windowGrid(win) {
    const out = [];
    const push = (mins) => {
        const value = minutesToTime(mins);
        if (!out.some(o => o.value === value)) out.push({ value, label: formatTime12h(value) });
    };
    push(win.open);
    for (let m = Math.ceil(win.open / 60) * 60; m < win.close; m += 60) push(m);
    push(win.close);
    return out;
}

/**
 * The times offered by a day-row's time dropdown.
 *
 * `anchor` is the PREVIOUS attempt (the 1st call for Retry 1's row), because
 * every retry is measured from the one before it.  The list is the first real
 * calling day at or after (anchor's day + dayOffset), legal-clamped, hour steps
 * anchored at the window open with the close always included — exactly what the
 * resolver accepts, so the picker and the Schedule Preview agree.
 *
 * "Same day" only offers times still ahead of the previous attempt; once that
 * day's window has passed it shows the next open day instead, which is where
 * the resolver would put it too.
 */
function buildTimeOptions(hours, anchor, dayOffset) {
    const tz = hoursTimezone(hours);
    const w = toWall(anchor, tz);

    const dayAt = (step) => {
        for (let i = 0; i < 7; i++) {
            const win = scheduleWindow(hours, DAYS[(w.weekday + step + i) % 7]);
            if (win) return { win, day: addDays(w, step + i) };
        }
        return null;
    };

    if (dayOffset === 0) {
        const same = dayAt(0);
        if (!same) return [];
        const isToday = same.day.y === w.y && same.day.mo === w.mo && same.day.d === w.d;
        if (!isToday) return windowGrid(same.win);            // today closed → that day's hours
        const prevMins = w.h * 60 + w.mi;
        const future = windowGrid(same.win).filter(o => timeToMinutes(o.value) > prevMins);
        if (future.length) return future;
        const next = dayAt(1);                                // window already gone → next open day
        return next ? windowGrid(next.win) : [];
    }

    const target = dayAt(dayOffset);
    return target ? windowGrid(target.win) : [];
}

function formatDayTime(date, tz) {
    const w = toWall(date, tz);
    return `${DAYS[w.weekday]} at ${formatTime12h(`${String(w.h).padStart(2, "0")}:${String(w.mi).padStart(2, "0")}`)}`;
}

function retrySubtitle(item, index) {
    if (!item) return "";
    if (item.type === "nextWindow") return "Try again when your calling hours open next.";
    if (item.type === "after") {
        if (index === 0) return "Try again after the first call.";
        const found = AFTER_OPTIONS.find(o => o.value === item.minutes);
        return `Try again ${found ? found.label.toLowerCase() : "later"}.`;
    }
    if (item.dayOffset === 0) return "Try again later the same day.";
    if (item.dayOffset === 1) return "Try again the next day.";
    return `Try again after ${item.dayOffset} days.`;
}

function safeTime(value) {
    if (value === "open") return "open";
    if (typeof value !== "string" || !/^\d{1,2}:\d{2}$/.test(value)) return "open";
    const parts = value.split(":");
    const mins = Number(parts[0]) * 60 + Number(parts[1]);
    return mins >= 0 && mins < 1440 ? minutesToTime(mins) : "open";
}

function normalizeRetryItem(raw, index) {
    const fallback = DEFAULT_RETRY_ITEMS[index] || DEFAULT_RETRY_ITEMS[DEFAULT_RETRY_ITEMS.length - 1];
    if (!raw || typeof raw !== "object") return { ...fallback };

    if (raw.type === "nextWindow") return { type: "nextWindow" };

    if (index === 0) {
        const minutes = Number(raw.minutes);
        return { type: "after", minutes: AFTER_MINUTES.includes(minutes) ? minutes : 120 };
    }

    const dayOffset = Math.max(0, Math.min(3, Number(raw.dayOffset) || 0));
    return { type: "day", dayOffset, time: safeTime(raw.time) };
}

function planFromSettings(data) {
    const items = Array.from({ length: RETRY_MAX }, (_, i) => normalizeRetryItem(DEFAULT_RETRY_ITEMS[i], i));

    const rawGaps = data?.retryGaps;
    const plan = (rawGaps && typeof rawGaps === "object" && !Array.isArray(rawGaps))
        ? rawGaps
        : (data?.retryPlan && typeof data.retryPlan === "object")
            ? data.retryPlan
            : null;

    if (plan && typeof plan === "object" && Array.isArray(plan.items)) {
        return {
            count: Math.max(0, Math.min(RETRY_MAX, Number(plan.count) || 0)),
            items: items.map((_, i) => normalizeRetryItem(plan.items[i], i)),
        };
    }

    if (!Array.isArray(rawGaps)) return { count: DEFAULT_RETRY_COUNT, items };

    const gaps = rawGaps.map(Number).filter(n => Number.isFinite(n) && n > 0);
    if (!gaps.length) return { count: 0, items };
    if (gaps.length === LEGACY_UNSET_GAPS.length && gaps.every((g, i) => g === LEGACY_UNSET_GAPS[i])) {
        return { count: DEFAULT_RETRY_COUNT, items };
    }

    const converted = items.map((_, i) => {
        if (i >= gaps.length) return items[i];
        const gap = gaps[i];
        if (i === 0) {
            if (gap > 360) return { type: "nextWindow" };
            const nearest = AFTER_MINUTES.reduce((a, b) => (Math.abs(b - gap) < Math.abs(a - gap) ? b : a), AFTER_MINUTES[0]);
            return { type: "after", minutes: Math.max(30, nearest) };
        }
        return { type: "day", dayOffset: Math.max(1, Math.min(3, Math.round(gap / 1440))), time: "open" };
    });
    return { count: Math.min(RETRY_MAX, gaps.length), items: converted };
}

function deriveLegacyGaps(hours, items) {
    if (!items.length) return [];
    const start = snapIntoWindow(hours, new Date());
    const times = [start, ...resolveLadder(hours, start, items)];
    const gaps = [];
    for (let i = 1; i < times.length; i++) {
        gaps.push(Math.max(1, Math.round((times[i].getTime() - times[i - 1].getTime()) / 60000)));
    }
    return gaps;
}

// ── Icons ────────────────────────────────────────────────────────────────
const PhoneIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);

const ClockIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const CalendarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const RefreshIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
);

const ChatIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const InfoIconMini = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);

const ChevronIcon = ({ open }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`${styles.chevronIcon} ${open ? styles.rotated : ""}`}
    >
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

// Outline neutral icons used inside the retry dropdowns
const ClockMini = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15.5 14" />
    </svg>
);

const CalMini = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <line x1="8" y1="3" x2="8" y2="7" />
        <line x1="16" y1="3" x2="16" y2="7" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const ChevronMini = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const CheckMini = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

/* ──────────────────────────────────────────────────────────────────────────
   MiniDropdown Component
   ────────────────────────────────────────────────────────────────────────── */
const MENU_MAX_HEIGHT = 264;

const MiniDropdown = ({ value, options, onChange, icon, label, className = "" }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const [flipUp, setFlipUp] = useState(false);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const selected = options.find(o => o.value === value) || null;

    const place = useCallback(() => {
        const rect = btnRef.current?.getBoundingClientRect();
        if (!rect) return;

        const width = Math.max(rect.width, 178);
        const maxLeft = Math.max(8, window.innerWidth - width - 8);
        const left = Math.min(Math.max(8, rect.left + rect.width - width), maxLeft);

        const below = window.innerHeight - rect.bottom - 12;
        const above = rect.top - 12;
        const up = below < Math.min(MENU_MAX_HEIGHT, 210) && above > below;

        setFlipUp(up);
        setPos(up
            ? { left, width, bottom: window.innerHeight - rect.top + 6, maxHeight: Math.max(140, Math.min(MENU_MAX_HEIGHT, above)) }
            : { left, width, top: rect.bottom + 6, maxHeight: Math.max(140, Math.min(MENU_MAX_HEIGHT, below)) });
    }, []);

    const close = useCallback(() => {
        setOpen(false);
    }, []);

    useEffect(() => {
        if (!open) return;

        const onOutside = (e) => {
            if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            close();
        };
        const onScroll = (e) => {
            if (menuRef.current && e.target && menuRef.current.contains(e.target)) return;
            close();
        };
        const onKey = (e) => {
            if (e.key === "Escape") { close(); btnRef.current?.focus(); }
        };

        document.addEventListener("mousedown", onOutside);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", close);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onOutside);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", close);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, close]);

    useEffect(() => {
        if (!open || !menuRef.current) return;
        const el = menuRef.current.querySelector('[data-selected="true"]');
        if (el) el.scrollIntoView({ block: "center" });
        menuRef.current.focus({ preventScroll: true });
    }, [open]);

    const onMenuKeyDown = (e) => {
        const index = options.findIndex(o => o.value === value);
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const step = e.key === "ArrowDown" ? 1 : -1;
            const next = options[Math.max(0, Math.min(options.length - 1, index + step))];
            if (next) onChange(next.value);
        } else if (e.key === "Enter" || e.key === "Tab") {
            close();
        }
    };

    return (
        <>
            <button
                type="button"
                ref={btnRef}
                className={`${styles.miniTrigger} ${className} ${open ? styles.miniTriggerOpen : ""}`}
                onClick={() => {
                    if (open) { close(); return; }
                    place();
                    setOpen(true);
                }}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={label}
            >
                {icon && <span className={styles.miniIcon}>{icon}</span>}
                <span className={styles.miniValue}>{selected ? selected.label : "Select"}</span>
                <span className={`${styles.miniChevron} ${open ? styles.miniChevronOpen : ""}`}>
                    <ChevronMini />
                </span>
            </button>

            {open && pos && createPortal(
                <div
                    ref={menuRef}
                    tabIndex={-1}
                    role="listbox"
                    className={`${styles.miniMenu} ${flipUp ? styles.miniMenuUp : ""}`}
                    style={pos}
                    onKeyDown={onMenuKeyDown}
                >
                    {options.length === 0 ? (
                        <div className={styles.miniEmpty}>Nothing available</div>
                    ) : options.map(option => {
                        const isSelected = option.value === value;
                        return (
                            <button
                                key={String(option.value)}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                data-selected={isSelected}
                                className={`${styles.miniOption} ${isSelected ? styles.miniOptionSelected : ""}`}
                                onClick={() => { onChange(option.value); close(); }}
                            >
                                <span>{option.label}</span>
                                {isSelected && <span className={styles.miniCheck}><CheckMini /></span>}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    );
};

/* ──────────────────────────────────────────────────────────────────────────
   Schedule Preview (Matches Image)
   ────────────────────────────────────────────────────────────────────────── */
const SchedulePreview = ({ hours, start, times, open, onToggle }) => {
    const tz = hoursTimezone(hours);

    return (
        <div className={`${styles.previewCard} ${open ? "" : styles.previewCardCollapsed}`}>
            <div
                className={styles.previewHead}
                onClick={onToggle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={open}
            >
                <div className={styles.iconBox}>
                    <CalendarIcon />
                </div>
                <div className={styles.itemText}>
                    <span className={styles.itemTitle}>Schedule Preview</span>
                    <span className={styles.itemSubtitle}>
                        Here&rsquo;s when the calls will happen based on your settings.
                    </span>
                </div>
                <div className={styles.chevron}>
                    <ChevronIcon open={open} />
                </div>
            </div>

            {open && (
                <>
                    <div className={styles.previewList}>
                        <div className={styles.previewLine}>
                            <div className={styles.previewMarker}>
                                <span className={`${styles.previewDot} ${styles.previewDotStart}`} />
                                {times.length > 0 && <span className={styles.previewLineBar} />}
                            </div>
                            <p className={styles.previewText}>
                                <span className={styles.previewMuted}>If the 1st call is on </span>
                                <span className={styles.previewDark}>{formatDayTime(start, tz)},</span>
                            </p>
                        </div>

                        {times.map((time, index) => {
                            const isLast = index === times.length - 1;
                            return (
                                <div className={styles.previewLine} key={index}>
                                    <div className={styles.previewMarker}>
                                        <span className={styles.previewDot} />
                                        {!isLast && <span className={styles.previewLineBar} />}
                                    </div>
                                    <p className={styles.previewText}>
                                        <span className={styles.previewMuted}>Retry {index + 1} will happen </span>
                                        <b className={styles.previewDarkBold}>{formatDayTime(time, tz)}</b>
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.previewNote}>
                        <span className={styles.previewNoteIcon}><InfoIconMini /></span>
                        <span className={styles.previewNoteText}>All retries happen only during your calling hours.</span>
                    </div>
                </>
            )}
        </div>
    );
};

const PounceSettings = () => {
    const navigate = useNavigate();
    const { agentId: paramAgentId } = useParams();
    const agentId =
        paramAgentId ||
        sessionStorage.getItem("SelectAgentId") ||
        localStorage.getItem("SelectAgentId") ||
        "";

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [openSection, setOpenSection] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(true);

    const [autoCallEnabled, setAutoCallEnabled] = useState(true);
    const [smsEnabled, setSmsEnabled] = useState(true);
    const [smsTemplate, setSmsTemplate] = useState(DEFAULT_SMS_TEMPLATE);

    const [retryCount, setRetryCount] = useState(DEFAULT_RETRY_COUNT);
    const [retryItems, setRetryItems] = useState(() => DEFAULT_RETRY_ITEMS.map((item, i) => normalizeRetryItem(item, i)));

    const [businessHours, setBusinessHours] = useState(DEFAULT_HOURS);

    const [timePicker, setTimePicker] = useState({
        open: false,
        day: null,
        field: null,
        selectedTime: null
    });

    const [tzDropdownOpen, setTzDropdownOpen] = useState(false);
    const [tzMenuPos, setTzMenuPos] = useState(null);
    const [tzSearch, setTzSearch] = useState("");
    const tzTriggerRef = useRef(null);
    const tzMenuRef = useRef(null);

    const timePickerListRef = useRef(null);
    const selectedTimeOptionRef = useRef(null);

    const autoCallDebounceRef = useRef(null);
    const autoCallLatestValueRef = useRef(autoCallEnabled);
    const autoCallRequestSeqRef = useRef(0);

    const initialSnapshotRef = useRef(null);
    const [nowTick, setNowTick] = useState(() => Date.now());

    /**
     * "Live" clock behind the Schedule Preview.
     *
     * The preview is always built from the CURRENT time (snapped into the
     * calling window), so it has to stay current:
     *   - the tick is aligned to the minute boundary, so the line flips the
     *     moment the clock's minute changes (not up to 60s late);
     *   - coming back to the tab (focus / visibility) re-reads the clock
     *     immediately instead of waiting for the next tick.
     */
    useEffect(() => {
        let intervalId = null;
        const toNextMinute = 60000 - (Date.now() % 60000) + 40;
        const timeoutId = setTimeout(() => {
            setNowTick(Date.now());
            intervalId = setInterval(() => setNowTick(Date.now()), 60000);
        }, toNextMinute);

        const refreshNow = () => setNowTick(Date.now());
        const onVisibility = () => {
            if (!document.hidden) refreshNow();
        };

        window.addEventListener("focus", refreshNow);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
            window.removeEventListener("focus", refreshNow);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    useEffect(() => {
        if (agentId) {
            loadSettings();
            loadStatus();
        }
    }, [agentId]);

    useEffect(() => {
        return () => {
            if (autoCallDebounceRef.current) {
                clearTimeout(autoCallDebounceRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!timePicker.open) return;
        const timer = setTimeout(() => {
            if (selectedTimeOptionRef.current && timePickerListRef.current) {
                selectedTimeOptionRef.current.scrollIntoView({
                    block: "center",
                    behavior: "auto"
                });
            }
        }, 0);
        return () => clearTimeout(timer);
    }, [timePicker.open, timePicker.day, timePicker.field]);

    const loadStatus = async () => {
        try {
            const res = await getPounceStatus();
            if (res.success) {
                setAutoCallEnabled(res.enabled !== false);
                autoCallLatestValueRef.current = res.enabled !== false;
            }
        } catch (err) {
            console.error("Failed to load status:", err);
        }
    };

    const loadSettings = async () => {
        try {
            setLoading(true);
            const res = await getPounceAgentSettings(agentId);
            if (res.success && res.data) {
                const smsOn = res.data.smsEnabled !== undefined ? res.data.smsEnabled : true;
                const template =
                    typeof res.data.smsTemplate === "string" ? res.data.smsTemplate : DEFAULT_SMS_TEMPLATE;
                const hoursData = res.data.callingHours || res.data.businessHours;
                const hours =
                    hoursData && typeof hoursData === "object" && hoursData.schedule
                        ? {
                            ...DEFAULT_HOURS,
                            ...hoursData,
                            schedule: {
                                ...DEFAULT_HOURS.schedule,
                                ...(hoursData.schedule || {})
                            }
                        }
                        : DEFAULT_HOURS;

                const plan = planFromSettings(res.data);

                setRetryCount(plan.count);
                setRetryItems(plan.items);
                setSmsEnabled(smsOn);
                setSmsTemplate(template);
                setBusinessHours(hours);

                initialSnapshotRef.current = JSON.stringify({
                    retryGaps: { count: plan.count, items: plan.items.slice(0, plan.count) },
                    smsEnabled: smsOn,
                    smsTemplate: template,
                    callingHours: hours
                });
            }
        } catch (err) {
            console.error("Failed to load settings:", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (section) => {
        setOpenSection(openSection === section ? null : section);
    };

    const toggleTzDropdown = () => {
        if (tzDropdownOpen) {
            setTzDropdownOpen(false);
            setTzSearch("");
            return;
        }
        const rect = tzTriggerRef.current?.getBoundingClientRect();
        if (rect) {
            setTzMenuPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
        }
        setTzSearch("");
        setTzDropdownOpen(true);
    };

    const selectTimezone = (value) => {
        setBusinessHours(prev => ({ ...prev, timezone: value }));
        setTzDropdownOpen(false);
        setTzSearch("");
    };

    useEffect(() => {
        if (!tzDropdownOpen) return;

        const handleOutside = (e) => {
            if (
                tzMenuRef.current &&
                !tzMenuRef.current.contains(e.target) &&
                !(tzTriggerRef.current && tzTriggerRef.current.contains(e.target))
            ) {
                setTzDropdownOpen(false);
            }
        };

        const handleScrollOrResize = (e) => {
            if (tzMenuRef.current && e.target && tzMenuRef.current.contains(e.target)) {
                return;
            }
            setTzDropdownOpen(false);
        };

        document.addEventListener("mousedown", handleOutside);
        window.addEventListener("scroll", handleScrollOrResize, true);
        window.addEventListener("resize", handleScrollOrResize);

        return () => {
            document.removeEventListener("mousedown", handleOutside);
            window.removeEventListener("scroll", handleScrollOrResize, true);
            window.removeEventListener("resize", handleScrollOrResize);
        };
    }, [tzDropdownOpen]);

    const handleAutoCallToggle = () => {
        const newValue = !autoCallEnabled;
        setAutoCallEnabled(newValue);
        autoCallLatestValueRef.current = newValue;

        if (autoCallDebounceRef.current) {
            clearTimeout(autoCallDebounceRef.current);
        }

        autoCallDebounceRef.current = setTimeout(async () => {
            const valueToSend = autoCallLatestValueRef.current;
            const mySeq = ++autoCallRequestSeqRef.current;

            try {
                await setPounceEnabled(valueToSend);
            } catch (err) {
                console.error("Failed to update auto-call:", err);
                if (mySeq === autoCallRequestSeqRef.current) {
                    setAutoCallEnabled(!valueToSend);
                    autoCallLatestValueRef.current = !valueToSend;
                }
            }
        }, 600);
    };

    const setRetryCountSafe = (next) => {
        setRetryCount(next);
        setRetryItems(prev => prev.map((item, i) => normalizeRetryItem(item, i)));
    };

    const updateRetry = (index, patch) => {
        setRetryItems(prev => prev.map((item, i) => (i === index ? normalizeRetryItem({ ...item, ...patch }, i) : item)));
    };

    const onRetry1Change = (value) => {
        updateRetry(0, value === "nextWindow" ? { type: "nextWindow" } : { type: "after", minutes: value });
    };

    const onRetryDayChange = (index, value) => {
        if (value === "nextWindow") {
            updateRetry(index, { type: "nextWindow" });
            return;
        }
        const current = retryItems[index];
        updateRetry(index, {
            type: "day",
            dayOffset: value,
            time: current?.type === "day" ? current.time : "10:00",
        });
    };

    const toggleDay = (day) => {
        setBusinessHours(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: { ...prev.schedule[day], closed: !prev.schedule[day].closed }
            }
        }));
    };

    /**
     * Turning "Same Schedule Everyday" ON copies the first open day's times to
     * every checked day, so what the rows show is exactly what the scheduler
     * uses.  Turning it OFF keeps the current rows as they are.
     */
    const toggleSameSchedule = () => {
        setBusinessHours(prev => {
            if (prev.sameSchedule) return { ...prev, sameSchedule: false };

            const reference = DAYS.map(d => prev.schedule[d]).find(d => d && !d.closed);
            if (!reference) return { ...prev, sameSchedule: true };

            const schedule = { ...prev.schedule };
            DAYS.forEach(day => {
                if (!schedule[day].closed) {
                    schedule[day] = { ...schedule[day], open: reference.open, close: reference.close };
                }
            });
            return { ...prev, sameSchedule: true, schedule };
        });
    };

    const updateTime = (day, field, value) => {
        setBusinessHours(prev => {
            const newSchedule = { ...prev.schedule };

            const applyUpdate = (d) => {
                const curr = newSchedule[d];
                let updated = { ...curr, [field]: value };

                if (field === "open") {
                    const openM = timeToMinutes(value);
                    const closeM = timeToMinutes(curr.close || "18:00");
                    if (closeM <= openM) {
                        updated.close = minutesToTime(openM + SLOT_STEP_MIN);
                    }
                }

                if (field === "close") {
                    const openM = timeToMinutes(curr.open || "09:00");
                    const closeM = timeToMinutes(value);
                    updated.close = closeM > openM ? value : minutesToTime(openM + SLOT_STEP_MIN);
                }

                newSchedule[d] = updated;
            };

            if (prev.sameSchedule) {
                DAYS.forEach(d => {
                    if (!newSchedule[d].closed) applyUpdate(d);
                });
            } else {
                applyUpdate(day);
            }

            return { ...prev, schedule: newSchedule };
        });
    };

    const openTimePicker = (day, field) => {
        const currentTime = businessHours.schedule[day][field];
        setTimePicker({
            open: true,
            day,
            field,
            selectedTime: currentTime
        });
    };

    const closeTimePicker = () => {
        setTimePicker(prev => ({ ...prev, open: false }));
    };

    const selectTime = (timeValue) => {
        if (timePicker.day && timePicker.field) {
            updateTime(timePicker.day, timePicker.field, timeValue);
        }
        closeTimePicker();
    };

    const getTimeLabel = (time24) => {
        const option = TIME_OPTIONS.find(t => t.value === time24);
        return option ? option.label : formatTime12h(time24);
    };

    const getFilteredTimeOptions = () => {
        if (!timePicker.day || timePicker.field !== "close") {
            return TIME_OPTIONS;
        }
        const openTime = businessHours.schedule[timePicker.day]?.open || "09:00";
        const openM = timeToMinutes(openTime);
        return TIME_OPTIONS.filter(opt => timeToMinutes(opt.value) > openM);
    };

    const applyPreset = (type) => {
        setBusinessHours(prev => {
            const updated = { ...prev.schedule };
            if (type === "weekdays") {
                DAYS.forEach((d, i) => {
                    updated[d] = i < 5
                        ? { open: "09:00", close: "18:00", closed: false }
                        : { ...updated[d], closed: true };
                });
            } else if (type === "everyday") {
                DAYS.forEach(d => {
                    updated[d] = { open: "09:00", close: "17:00", closed: false };
                });
            } else if (type === "weekdays9to6") {
                DAYS.forEach((d, i) => {
                    updated[d] = i < 5
                        ? { open: "09:00", close: "18:00", closed: false }
                        : { open: "10:00", close: "16:00", closed: true };
                });
            }
            return { ...prev, schedule: updated };
        });
    };

    const activeItems = useMemo(() => retryItems.slice(0, retryCount), [retryItems, retryCount]);

    /**
     * The preview always shows the SAME numbers the engine will use:
     * the 1st call is snapped into the calling window, then every retry is
     * resolved through the calling days / hours (closed days roll forward,
     * times land inside the window only).
     */
    const preview = useMemo(() => {
        const start = snapIntoWindow(businessHours, new Date(nowTick));
        return { start, times: resolveLadder(businessHours, start, activeItems) };
    }, [businessHours, activeItems, nowTick]);

    const timeOptionsForRow = useCallback((index) => {
        const item = retryItems[index];
        const dayOffset = item?.type === "day" ? item.dayOffset : 1;
        // chained: this row's day/time is measured from the previous attempt
        const cursor = index === 0 ? preview.start : (preview.times[index - 1] || preview.start);
        const options = buildTimeOptions(businessHours, cursor, dayOffset);
        if (item?.type === "day" && item.time && item.time !== "open" && !options.some(o => o.value === item.time)) {
            options.unshift({ value: item.time, label: formatTime12h(item.time) });
        }
        return options;
    }, [retryItems, preview, businessHours]);

    const handleSave = async () => {
        const hasOpenDay = Object.values(businessHours.schedule).some(d => !d.closed);
        if (!hasOpenDay) {
            setOpenSection("hours");
            return;
        }

        try {
            setSaving(true);
            const items = activeItems.map(item => ({ ...item }));

            const res = await updatePounceAgentSettings(agentId, {
                retryGaps: { count: retryCount, items },
                smsEnabled: smsEnabled,
                smsTemplate: smsTemplate,
                callingHours: businessHours
            });

            if (res.success) {
                initialSnapshotRef.current = JSON.stringify({
                    retryGaps: { count: retryCount, items },
                    smsEnabled,
                    smsTemplate,
                    callingHours: businessHours
                });
            } else {
                console.error("Failed to save:", res.message);
            }
        } catch (err) {
            console.error("Save error:", err);
        } finally {
            setSaving(false);
        }
    };

    const getTimezoneLabel = () => {
        const currentTz = businessHours.timezone;
        if (!currentTz) return "Select timezone";
        const found = TIMEZONE_OPTIONS.find(t => t.value === currentTz);
        return found ? `${found.label} (${currentTz})` : currentTz;
    };

    const getHoursSummary = () => {
        const openDays = DAYS.filter(d => !businessHours.schedule[d]?.closed);
        if (openDays.length === 0) return "No days selected";
        if (openDays.length === 7) return "Every day";

        const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        const hasWeekdays = weekdays.every(d => openDays.includes(d));
        const hasWeekend = ["Saturday", "Sunday"].every(d => openDays.includes(d));

        if (hasWeekdays && !hasWeekend) {
            const first = businessHours.schedule.Monday;
            return `Mon – Fri, ${formatTime12h(first.open)} – ${formatTime12h(first.close)}`;
        }
        if (hasWeekdays && hasWeekend) return "Every day";

        const firstOpen = openDays[0];
        const lastOpen = openDays[openDays.length - 1];
        const shortFirst = DAYS_SHORT[DAYS.indexOf(firstOpen)];
        const shortLast = DAYS_SHORT[DAYS.indexOf(lastOpen)];
        const time = businessHours.schedule[firstOpen];
        return `${shortFirst} – ${shortLast}, ${formatTime12h(time.open)} – ${formatTime12h(time.close)}`;
    };

    const getRetrySummary = () => {
        if (retryCount === 0) return "No retries (1 attempt only)";
        return `${retryCount + 1} attempts total`;
    };

    const isDirty =
        initialSnapshotRef.current !== null &&
        JSON.stringify({
            retryGaps: { count: retryCount, items: activeItems },
            smsEnabled,
            smsTemplate,
            callingHours: businessHours
        }) !== initialSnapshotRef.current;

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loading}>Loading...</div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                    </svg>
                </button>
                <h1 className={styles.title}>Calling Settings</h1>
                <p className={styles.subtitle}>Control when and how Pounce follows up with leads.</p>
            </div>

            {/* Settings List */}
            <div className={styles.list}>

                {/* Auto-call Toggle */}
                <div className={styles.item}>
                    <div className={styles.itemHeader}>
                        <div className={styles.iconBox}>
                            <PhoneIcon />
                        </div>
                        <div className={styles.itemText}>
                            <span className={styles.itemTitle}>Auto-call</span>
                            <span className={styles.itemSubtitle}>Automatically call eligible leads</span>
                        </div>
                        <button
                            className={`${styles.toggle} ${autoCallEnabled ? styles.on : ""}`}
                            onClick={handleAutoCallToggle}
                            aria-label="Toggle auto-call"
                        >
                            <span className={styles.knob}></span>
                        </button>
                    </div>
                </div>

                {/* Timezone */}
                <div className={`${styles.item} ${openSection === "timezone" ? styles.open : ""}`}>
                    <div
                        className={styles.itemHeader}
                        onClick={() => toggleSection("timezone")}
                    >
                        <div className={styles.iconBox}>
                            <ClockIcon />
                        </div>
                        <div className={styles.itemText}>
                            <span className={styles.itemTitle}>Timezone</span>
                            <span className={styles.itemSubtitle}>{getTimezoneLabel()}</span>
                        </div>
                        <div className={styles.chevron}>
                            <ChevronIcon open={openSection === "timezone"} />
                        </div>
                    </div>
                    {openSection === "timezone" && (
                        <div className={styles.expanded}>
                            <div className={styles.dropdownWrap}>
                                <button
                                    type="button"
                                    ref={tzTriggerRef}
                                    className={`${styles.dropdownTrigger} ${tzDropdownOpen ? styles.dropdownTriggerOpen : ""}`}
                                    onClick={toggleTzDropdown}
                                >
                                    <span>{getTimezoneLabel()}</span>
                                    <span className={`${styles.dropdownArrow} ${tzDropdownOpen ? styles.dropdownArrowOpen : ""}`}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                    {openSection === "timezone" && tzDropdownOpen && tzMenuPos && createPortal(
                        <div
                            ref={tzMenuRef}
                            className={styles.dropdownMenu}
                            style={{ top: tzMenuPos.top, left: tzMenuPos.left, width: tzMenuPos.width }}
                        >
                            <div className={styles.tzSearchWrap} onClick={e => e.stopPropagation()}>
                                <input
                                    type="text"
                                    className={styles.tzSearchInput}
                                    placeholder="Search timezone or city..."
                                    value={tzSearch}
                                    onChange={e => setTzSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {(() => {
                                const q = tzSearch.trim().toLowerCase();
                                const list = [...TIMEZONE_OPTIONS];
                                if (businessHours.timezone && !list.some(t => t.value === businessHours.timezone)) {
                                    list.unshift({
                                        value: businessHours.timezone,
                                        label: businessHours.timezone,
                                        sub: "Current timezone"
                                    });
                                }

                                const filtered = q
                                    ? list.filter(t =>
                                        t.label.toLowerCase().includes(q) ||
                                        t.value.toLowerCase().includes(q) ||
                                        (t.sub && t.sub.toLowerCase().includes(q))
                                    )
                                    : list;

                                if (filtered.length === 0) {
                                    return <div className={styles.tzNoResults}>No matching timezone</div>;
                                }

                                return filtered.map(tz => {
                                    const isSelected = businessHours.timezone === tz.value;
                                    return (
                                        <button
                                            key={tz.value}
                                            type="button"
                                            className={`${styles.tzOption} ${isSelected ? styles.tzOptionSelected : ""}`}
                                            onClick={() => selectTimezone(tz.value)}
                                        >
                                            <div className={styles.tzOptionText}>
                                                <span className={styles.tzOptionMain}>{tz.label}</span>
                                                <span className={styles.tzOptionSub}>{tz.sub} · {tz.value}</span>
                                            </div>
                                            {isSelected && (
                                                <svg className={styles.tzCheck} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </button>
                                    );
                                });
                            })()}
                        </div>,
                        document.body
                    )}
                </div>

                {/* Calling Hours */}
                <div className={`${styles.item} ${openSection === "hours" ? styles.open : ""}`}>
                    <div
                        className={styles.itemHeader}
                        onClick={() => toggleSection("hours")}
                    >
                        <div className={styles.iconBox}>
                            <CalendarIcon />
                        </div>
                        <div className={styles.itemText}>
                            <span className={styles.itemTitle}>Calling Window</span>
                            <span className={styles.itemSubtitle}>{getHoursSummary()}</span>
                        </div>
                        <div className={styles.chevron}>
                            <ChevronIcon open={openSection === "hours"} />
                        </div>
                    </div>
                    {openSection === "hours" && (
                        <div className={styles.expanded}>
                            <div className={styles.sameScheduleRow}>
                                <div className={styles.sameScheduleText}>
                                    <span className={styles.sameScheduleTitle}>Same Schedule Everyday</span>
                                    <span className={styles.sameScheduleDesc}>Apply same daily schedule to all the days selected</span>
                                </div>
                                <button
                                    className={`${styles.toggle} ${businessHours.sameSchedule ? styles.on : ""}`}
                                    onClick={toggleSameSchedule}
                                    aria-label="Toggle same schedule every day"
                                >
                                    <span className={styles.knob}></span>
                                </button>
                            </div>

                            <div className={styles.daysList}>
                                {DAYS.map((day) => {
                                    const d = businessHours.schedule[day];
                                    const isOpen = !d.closed;
                                    return (
                                        <div key={day} className={styles.dayRow}>
                                            <label className={styles.dayCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    checked={isOpen}
                                                    onChange={() => toggleDay(day)}
                                                />
                                                <span className={styles.checkmark}></span>
                                            </label>
                                            <span className={styles.dayName}>{day}</span>
                                            <div className={styles.timeInputs}>
                                                <button
                                                    className={`${styles.timeBtn} ${!isOpen ? styles.disabled : ""}`}
                                                    onClick={() => isOpen && openTimePicker(day, "open")}
                                                    disabled={!isOpen}
                                                >
                                                    {getTimeLabel(d.open)}
                                                </button>
                                                <span className={styles.dash}>-</span>
                                                <button
                                                    className={`${styles.timeBtn} ${!isOpen ? styles.disabled : ""}`}
                                                    onClick={() => isOpen && openTimePicker(day, "close")}
                                                    disabled={!isOpen}
                                                >
                                                    {getTimeLabel(d.close)}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className={styles.quickBtns}>
                                <button className={styles.quickBtn} onClick={() => applyPreset("weekdays9to6")}>
                                    Weekdays 9-6
                                </button>
                                <button className={styles.quickBtn} onClick={() => applyPreset("everyday")}>
                                    Every Day
                                </button>
                            </div>

                            <p className={styles.hint}>Legal calling hours: 8:00 AM – 9:00 PM local time</p>
                        </div>
                    )}
                </div>

                {/* Additional Retries (Card 1 in Image) */}
                <div className={`${styles.item} ${openSection === "retry" ? styles.open : ""}`}>
                    <div
                        className={styles.itemHeader}
                        onClick={() => toggleSection("retry")}
                    >
                        <div className={styles.iconBox}>
                            <RefreshIcon />
                        </div>
                        <div className={styles.itemText}>
                            <span className={styles.itemTitle}>Additional Retries</span>
                            <span className={styles.itemSubtitle}>
                                {openSection === "retry"
                                    ? "Select how many times to retry if there's no answer."
                                    : getRetrySummary()}
                            </span>
                        </div>
                        <div className={styles.chevron}>
                            <ChevronIcon open={openSection === "retry"} />
                        </div>
                    </div>

                    {openSection === "retry" && (
                        <div className={styles.retryPanel}>
                            {/* Segmented Picker 0-4 */}
                            <div className={styles.retryCountBar} role="group" aria-label="How many retries">
                                <span
                                    className={styles.retryCountGlider}
                                    style={{ transform: `translateX(${retryCount * 100}%)` }}
                                />
                                {Array.from({ length: RETRY_MAX + 1 }, (_, n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`${styles.retryCountBtn} ${retryCount === n ? styles.retryCountActive : ""}`}
                                        aria-pressed={retryCount === n}
                                        onClick={() => setRetryCountSafe(n)}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>

                            {retryCount === 0 ? (
                                <p className={styles.retryZeroNote}>
                                    No retries — only the 1st call will be made.
                                </p>
                            ) : (
                                <div className={styles.retryRows}>
                                    {activeItems.map((item, index) => {
                                        const isFirst = index === 0;
                                        return (
                                            <div
                                                key={index}
                                                className={styles.retryRowNew}
                                                style={{ animationDelay: `${index * 45}ms` }}
                                            >
                                                <div className={styles.retryBadgeCol}>
                                                    <span className={styles.retryBadge}>{index + 1}</span>
                                                </div>

                                                <div className={styles.retryRowText}>
                                                    <span className={styles.retryRowTitle}>Retry {index + 1}</span>
                                                    <span className={styles.retryRowSub}>{retrySubtitle(item, index)}</span>
                                                </div>

                                                <div className={styles.retryRowControls}>
                                                    {isFirst ? (
                                                        <MiniDropdown
                                                            className={styles.miniTriggerSingle}
                                                            icon={<ClockMini />}
                                                            label={`Retry ${index + 1} timing`}
                                                            value={item.type === "nextWindow" ? "nextWindow" : item.minutes}
                                                            options={AFTER_OPTIONS}
                                                            onChange={onRetry1Change}
                                                        />
                                                    ) : (
                                                        <>
                                                            <MiniDropdown
                                                                className={item.type === "nextWindow" ? styles.miniTriggerSingle : styles.miniTriggerDay}
                                                                icon={<CalMini />}
                                                                label={`Retry ${index + 1} day`}
                                                                value={item.type === "nextWindow" ? "nextWindow" : item.dayOffset}
                                                                options={DAY_OPTIONS}
                                                                onChange={(value) => onRetryDayChange(index, value)}
                                                            />
                                                            {item.type !== "nextWindow" && (
                                                                <MiniDropdown
                                                                    className={styles.miniTriggerTime}
                                                                    icon={<ClockMini />}
                                                                    label={`Retry ${index + 1} time`}
                                                                    value={item.time === "open" ? (timeOptionsForRow(index)[0]?.value || "09:00") : item.time}
                                                                    options={timeOptionsForRow(index)}
                                                                    onChange={(value) => updateRetry(index, { type: "day", time: value })}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Schedule Preview (Card 2 in Image) */}
                {openSection === "retry" && (
                    <SchedulePreview
                        hours={businessHours}
                        start={preview.start}
                        times={preview.times}
                        open={previewOpen}
                        onToggle={() => setPreviewOpen(v => !v)}
                    />
                )}

                {/* SMS Follow-up */}
                <div className={styles.item}>
                    <div className={styles.itemHeader}>
                        <div className={styles.iconBox}>
                            <ChatIcon />
                        </div>
                        <div className={styles.itemText}>
                            <span className={styles.itemTitle}>SMS Follow-up</span>
                            <span className={styles.itemSubtitle}>
                                {"Send a text if no answer"}
                            </span>
                        </div>
                        <button
                            className={`${styles.toggle} ${smsEnabled ? styles.on : ""}`}
                            onClick={() => setSmsEnabled(!smsEnabled)}
                            aria-label="Toggle SMS"
                        >
                            <span className={styles.knob}></span>
                        </button>
                    </div>
                    {smsEnabled && (
                        <div className={styles.smsMessageWrap}>
                            <span className={styles.smsMessageLabel}>SMS Message</span>
                            <div className={styles.smsMessageBox}>
                                <textarea
                                    className={styles.smsMessageTextarea}
                                    value={smsTemplate}
                                    onChange={(e) => setSmsTemplate(e.target.value)}
                                    maxLength={SMS_MAX_CHARS}
                                    rows={3}
                                    placeholder={DEFAULT_SMS_TEMPLATE}
                                />
                                <span className={styles.smsMessageCounter}>
                                    {smsTemplate.length}/{SMS_MAX_CHARS}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

            </div>

            {/* Info Box */}
            <div className={styles.infoBox}>
                <div className={styles.infoIcon}>
                    <InfoIconMini />
                </div>
                <p>Pounce contacts only eligible leads within your calling window.</p>
            </div>

            {/* Save Button */}
            <div className={styles.saveBar}>
                <button
                    className={styles.saveBtn}
                    onClick={handleSave}
                    disabled={saving || !isDirty}
                >
                    {saving ? "Saving..." : "Save Settings"}
                </button>
            </div>

            {/* Time Picker Modal */}
            {timePicker.open && (
                <div className={styles.timePickerOverlay} onClick={closeTimePicker}>
                    <div className={styles.timePickerModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.timePickerHeader}>
                            <span>Select {timePicker.field === "open" ? "Start" : "End"} Time</span>
                            <button onClick={closeTimePicker} className={styles.closeBtn}>×</button>
                        </div>
                        <div className={styles.timePickerList} ref={timePickerListRef}>
                            {getFilteredTimeOptions().map(option => {
                                const isSelected = timePicker.selectedTime === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        ref={isSelected ? selectedTimeOptionRef : null}
                                        className={`${styles.timeOption} ${isSelected ? styles.selected : ""}`}
                                        onClick={() => selectTime(option.value)}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PounceSettings;
